// GRT Postback — Reporting table for GRT conversion postbacks.
//
// One row per enrollment postback received on the public webhook
// (POST /v1/postback/grt/{token} → grt_postbacks table), read back via
// GET /api/grt-postbacks. This is a conversion LEDGER view — no lifecycle,
// no mutation; just filter, inspect, export.
//
// Fetching (Logs.tsx pattern): a single load() per filter change, capped at
// 1,000 rows (the route's hard limit; `limited` flags the cap). Server-side
// filters are the TIME RANGE and CAMPAIGN only. The free-text SEARCH is
// purely client-side — it narrows the already-loaded rows in a useMemo
// (first/last name, phone, phone digits, GRT ID, agent, city) so typing
// never triggers a refetch.
//
// Time selection mirrors SmsDashboard: presets (Today PT / 24h / 7d / 30d)
// plus a custom start/end date range, resolved to the shared TimeSel wire
// shape (epoch SECONDS) by a local toTimeSel copy. toTimeSel runs inside
// load() — not memoized per render — so Refresh re-anchors "now" instead of
// replaying a stale Today-window.
//
// exactOptionalPropertyTypes is ON: the api params object is built
// conditionally (campaign omitted for the "all" sentinel, never undefined).

import { useCallback, useEffect, useMemo, useState } from "react";

import { api, type GrtPostbackRecord, type TimeSel } from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  inputStyle,
  PageHeader,
  Pill,
  Skeleton,
  tokens,
} from "../lib/theme";

// Max rows requested per fetch (also the route's cap — see admin-api
// routes/grt_postbacks.ts). Above this the server sets `limited: true`.
const FETCH_LIMIT = 1000;

// "Today (PT)" is the special sentinel: its range is [PT midnight today, now].
// The other presets are rolling-window seconds. (Copied from SmsDashboard.)
const TODAY_PT = -1;

const PRESETS: Array<{ label: string; sec: number }> = [
  { label: "Today (PT)", sec: TODAY_PT }, // special: since PT (America/Los_Angeles) midnight
  { label: "Last 24 hours", sec: 24 * 3600 },
  { label: "Last 7 days", sec: 7 * 24 * 3600 },
  { label: "Last 30 days", sec: 30 * 24 * 3600 },
];

/**
 * Epoch SECONDS of the most recent America/Los_Angeles (Pacific) midnight at
 * or before `nowMs` (the start of "today" in PT). DST-correct on EVERY day,
 * INCLUDING the two transition days: we derive the PT CALENDAR DATE for
 * `nowMs`, then resolve the exact instant of 00:00:00 on that PT date by
 * measuring PT's actual UTC offset at that instant via Intl (never a fixed
 * -7/-8). Local copy of the SmsDashboard helper.
 */
function ptMidnightTodaySec(nowMs: number = Date.now()): number {
  const TZ = "America/Los_Angeles";
  // 1. Today's PT calendar date (Y-M-D) for the given instant.
  const dateParts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(nowMs));
  const dp = (t: string): number =>
    Number.parseInt(dateParts.find((p) => p.type === t)?.value ?? "0", 10);
  const y = dp("year");
  const mo = dp("month");
  const d = dp("day");

  // 2. PT's UTC offset (minutes) AT this instant — found by formatting `nowMs`
  //    in PT and comparing to the same wall-clock read as UTC. (PST=-480, PDT=-420.)
  const offsetMinutesAt = (ms: number): number => {
    const p = new Intl.DateTimeFormat("en-US", {
      timeZone: TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(new Date(ms));
    const v = (t: string): number =>
      Number.parseInt(p.find((x) => x.type === t)?.value ?? "0", 10);
    // The wall-clock PT reading, interpreted as if it were UTC.
    const asUtc = Date.UTC(
      v("year"),
      v("month") - 1,
      v("day"),
      v("hour") % 24,
      v("minute"),
      v("second"),
    );
    return Math.round((asUtc - ms) / 60000);
  };

  // 3. Candidate PT-midnight epoch using the offset measured at `nowMs`, then
  //    a one-step correction using the offset measured AT the candidate
  //    (handles a transition occurring between midnight and now).
  const guessUtcMs = Date.UTC(y, mo - 1, d, 0, 0, 0);
  const off1 = offsetMinutesAt(nowMs);
  let midnightMs = guessUtcMs - off1 * 60000;
  const off2 = offsetMinutesAt(midnightMs);
  if (off2 !== off1) {
    midnightMs = guessUtcMs - off2 * 60000;
  }
  return Math.floor(midnightMs / 1000);
}

/** Resolve the selected preset/custom into a TimeSel for the API. */
function toTimeSel(
  presetSec: number,
  custom: { start: string; end: string } | null,
): TimeSel {
  if (custom && custom.start && custom.end) {
    const startSec = Math.floor(new Date(custom.start + "T00:00:00Z").getTime() / 1000);
    const endSec = Math.floor(new Date(custom.end + "T23:59:59Z").getTime() / 1000);
    return { kind: "range", startSec, endSec };
  }
  if (presetSec === TODAY_PT) {
    // "Today (PT)" — [PT midnight today, now], epoch seconds (wire format).
    const now = Date.now();
    return {
      kind: "range",
      startSec: ptMidnightTodaySec(now),
      endSec: Math.floor(now / 1000),
    };
  }
  return { kind: "window", windowSec: presetSec };
}

/** received_at_ms → local date + time (viewer's timezone). */
function fmtReceived(ms: number): string {
  const d = new Date(ms);
  return `${d.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  })} ${d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })}`;
}

/** first + last, whichever is present; "" when both null (render "—"). */
function fmtName(r: GrtPostbackRecord): string {
  return [r.first_name, r.last_name].filter(Boolean).join(" ");
}

/** 14897 → "$14,897" (thousands separators, no forced decimals). */
function fmtDebt(n: number): string {
  return `$${n.toLocaleString()}`;
}

function csvEscape(v: string | number | null): string {
  if (v === null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(entries: GrtPostbackRecord[]): string {
  // One CSV column per table column (received as unambiguous ISO instead of
  // the viewer-local rendering).
  const header = [
    "received_at",
    "name",
    "phone",
    "state",
    "campaign",
    "enrolled_debt",
    "agent",
    "grt_id",
    "fp_date",
    "rec_start_date",
  ].join(",");
  const rows = entries.map((r) =>
    [
      new Date(r.received_at_ms).toISOString(),
      fmtName(r),
      r.phone_number,
      r.state,
      r.campaign,
      r.enrolled_debt,
      r.agent,
      r.grt_id,
      r.fp_date,
      r.rec_start_date,
    ]
      .map(csvEscape)
      .join(","),
  );
  return [header, ...rows].join("\n");
}

export default function GrtPostback() {
  // Filters. campaign: null = the "all" sentinel (no server filter).
  const [presetSec, setPresetSec] = useState<number>(7 * 24 * 3600); // default: Last 7 days (route default)
  const [custom, setCustom] = useState<{ start: string; end: string } | null>(null);
  const [campaign, setCampaign] = useState<string | null>(null);
  const [q, setQ] = useState(""); // client-side only — never sent to the server

  const [entries, setEntries] = useState<GrtPostbackRecord[] | null>(null);
  const [limited, setLimited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    // exactOptionalPropertyTypes: build the params object conditionally —
    // omit campaign entirely for "all", never pass `campaign: undefined`.
    const params: { time: TimeSel; campaign?: string; limit: number } = {
      time: toTimeSel(presetSec, custom),
      limit: FETCH_LIMIT,
    };
    if (campaign) params.campaign = campaign;
    api
      .listGrtPostbacks(params)
      .then((d) => {
        setEntries(d.entries);
        setLimited(d.limited);
        setError(null);
        setRefreshedAt(new Date());
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => setLoading(false));
  }, [presetSec, custom, campaign]);

  useEffect(() => {
    load();
  }, [load]);

  // Client-side free-text search over the loaded rows (case-insensitive
  // substring across the PRP's search fields). No refetch on keystrokes.
  const filtered = useMemo(() => {
    if (!entries) return null;
    const needle = q.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter((r) =>
      [
        r.first_name,
        r.last_name,
        r.phone_number,
        r.phone_digits,
        r.grt_id,
        r.agent,
        r.city,
      ].some((f) => f != null && f.toLowerCase().includes(needle)),
    );
  }, [entries, q]);

  // Distinct non-null campaigns from the loaded rows. When a campaign filter
  // is active the response only contains that campaign — keep the selected
  // value present (and any others we saw) so the select never loses it.
  const campaignOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of entries ?? []) {
      if (r.campaign) set.add(r.campaign);
    }
    if (campaign) set.add(campaign);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [entries, campaign]);

  const exportCsv = useCallback(() => {
    if (!filtered || filtered.length === 0) return;
    const blob = new Blob([toCsv(filtered)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `grt-postbacks-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [filtered]);

  const selectStyle: React.CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.sm}px ${tokens.space.md}px`,
    fontSize: tokens.font.size.sm,
    cursor: "pointer",
  };

  return (
    <div>
      <PageHeader
        title="GRT Postback"
        subtitle="Conversion postbacks received from GRT"
        actions={
          <div
            style={{
              display: "flex",
              gap: tokens.space.sm,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <select
              value={custom ? "custom" : String(presetSec)}
              onChange={(e) => {
                if (e.target.value === "custom") {
                  const today = new Date().toISOString().slice(0, 10);
                  setCustom({ start: today, end: today });
                } else {
                  setCustom(null);
                  setPresetSec(Number(e.target.value));
                }
              }}
              style={selectStyle}
              aria-label="Time range"
            >
              {PRESETS.map((p) => (
                <option key={p.label} value={String(p.sec)}>
                  {p.label}
                </option>
              ))}
              <option value="custom">Custom range…</option>
            </select>
            {custom && (
              <>
                <input
                  type="date"
                  value={custom.start}
                  max={custom.end}
                  onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                  style={selectStyle}
                  aria-label="Start date"
                />
                <span style={{ color: tokens.color.fg2 }}>→</span>
                <input
                  type="date"
                  value={custom.end}
                  min={custom.start}
                  onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                  style={selectStyle}
                  aria-label="End date"
                />
              </>
            )}
            <select
              value={campaign ?? "all"}
              onChange={(e) => setCampaign(e.target.value === "all" ? null : e.target.value)}
              style={selectStyle}
              aria-label="Campaign"
            >
              <option value="all">All campaigns</option>
              {campaignOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, phone, GRT ID…"
              style={{ ...inputStyle, width: 230, padding: "8px 12px" }}
              aria-label="Search"
            />
            <button
              type="button"
              onClick={exportCsv}
              disabled={!filtered || filtered.length === 0}
              style={buttonStyle("ghost", !filtered || filtered.length === 0)}
              aria-label="Export CSV"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              style={buttonStyle("ghost", loading)}
              aria-label="Refresh"
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
            {refreshedAt && (
              <span style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
                Updated{" "}
                {refreshedAt.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            )}
          </div>
        }
      />

      {loading && !entries && (
        <Card>
          <Skeleton height={40} />
          <div style={{ height: 10 }} />
          <Skeleton height={40} />
          <div style={{ height: 10 }} />
          <Skeleton height={40} />
        </Card>
      )}

      {error && !entries && (
        <Card>
          <EmptyState title="Failed to load postbacks" hint={error} />
        </Card>
      )}

      {filtered && filtered.length === 0 && !loading && (
        <Card>
          <EmptyState
            title="No postbacks"
            hint={
              entries && entries.length > 0
                ? `No rows match the search — ${entries.length.toLocaleString()} in the window; clear the search to see them.`
                : "Nothing was received for the selected time range and campaign."
            }
          />
        </Card>
      )}

      {filtered && filtered.length > 0 && (
        <Card padding={0}>
          {limited && (
            <div
              style={{
                padding: tokens.space.sm,
                fontSize: tokens.font.size.xs,
                color: tokens.color.warning,
                borderBottom: `1px solid ${tokens.color.border}`,
              }}
            >
              Showing the most recent {(entries?.length ?? 0).toLocaleString()} postbacks —
              narrow the window to see all.
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: tokens.font.size.sm,
              }}
            >
              <thead>
                <tr>
                  <Th>Received</Th>
                  <Th>Name</Th>
                  <Th>Phone</Th>
                  <Th>State</Th>
                  <Th>Campaign</Th>
                  <Th align="right">Enrolled Debt</Th>
                  <Th>Agent</Th>
                  <Th>GRT ID</Th>
                  <Th>FP Date</Th>
                  <Th>Rec Start</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => (
                  <tr
                    key={r.grt_id}
                    style={{
                      background:
                        idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                    }}
                  >
                    <Td>{fmtReceived(r.received_at_ms)}</Td>
                    <Td>{fmtName(r) || "—"}</Td>
                    <Td mono>{r.phone_number ?? "—"}</Td>
                    <Td>{r.state ?? "—"}</Td>
                    <Td>
                      {r.campaign ? <Pill tone="neutral">{r.campaign}</Pill> : "—"}
                    </Td>
                    <Td align="right" mono>
                      {r.enrolled_debt != null ? (
                        <span style={{ fontVariantNumeric: "tabular-nums" }}>
                          {fmtDebt(r.enrolled_debt)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </Td>
                    <Td>{r.agent ?? "—"}</Td>
                    <Td mono>
                      {/* Synthetic ids (blank GRT_ID on the wire) render dimmed. */}
                      {r.grt_id.startsWith("noid:") ? (
                        <span style={{ color: tokens.color.fg2 }}>{r.grt_id}</span>
                      ) : (
                        r.grt_id
                      )}
                    </Td>
                    <Td mono>{r.fp_date ?? "—"}</Td>
                    <Td mono>{r.rec_start_date ?? "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg2,
        fontWeight: 600,
        fontSize: tokens.font.size.xs,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBottom: `1px solid ${tokens.color.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg1,
        borderBottom: `1px solid ${tokens.color.border}`,
        whiteSpace: "nowrap",
        fontFamily: mono ? tokens.font.mono : undefined,
        fontSize: mono ? tokens.font.size.xs : undefined,
      }}
    >
      {children}
    </td>
  );
}
